/**
 * DestinationsTab Component
 * Manages study destinations/countries with CRUD operations
 */
import { useState } from 'react';
import { Plus, Trash2, AlertTriangle, Globe, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { destinationsAPI } from '@/lib/api';

export const DestinationsTab = ({ 
  destinations, 
  universities,
  onRefresh, 
  user 
}) => {
  const [showAddDestination, setShowAddDestination] = useState(false);
  const [showDeleteDestConfirm, setShowDeleteDestConfirm] = useState(false);
  const [deletingDestination, setDeletingDestination] = useState(null);
  const [isDeletingDest, setIsDeletingDest] = useState(false);
  const [isAddingDest, setIsAddingDest] = useState(false);
  const [newDestination, setNewDestination] = useState({
    country: '',
    flag_emoji: '',
    avg_tuition_range: '',
    currency: '',
    description: ''
  });

  const openDeleteDestConfirm = (dest) => {
    setDeletingDestination(dest);
    setShowDeleteDestConfirm(true);
  };

  const handleDeleteDestination = async () => {
    if (!deletingDestination) return;
    setIsDeletingDest(true);
    try {
      await destinationsAPI.delete(deletingDestination.id);
      toast.success(`${deletingDestination.country} deleted successfully`);
      setShowDeleteDestConfirm(false);
      setDeletingDestination(null);
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete destination');
    } finally {
      setIsDeletingDest(false);
    }
  };

  const handleAddDestination = async () => {
    if (!newDestination.country) {
      toast.error('Country name is required');
      return;
    }
    setIsAddingDest(true);
    try {
      await destinationsAPI.create(newDestination);
      toast.success(`${newDestination.country} added successfully`);
      setShowAddDestination(false);
      setNewDestination({
        country: '',
        flag_emoji: '',
        avg_tuition_range: '',
        currency: '',
        description: ''
      });
      onRefresh?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add destination');
    } finally {
      setIsAddingDest(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Manage Countries</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {destinations.length} destinations available
          </p>
        </div>
        {user?.is_super_admin && (
          <Button 
            onClick={() => setShowAddDestination(true)}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="add-destination-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Country
          </Button>
        )}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {destinations.map((dest) => (
          <div key={dest.id} className="bg-gray-50 dark:bg-slate-700 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{dest.flag_emoji || '🌍'}</span>
              <div>
                <p className="font-medium text-gray-800 dark:text-white">{dest.country}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {universities.filter(u => u.country === dest.country).length} universities
                </p>
              </div>
            </div>
            {user?.is_super_admin && (
              <Button
                size="icon"
                variant="ghost"
                onClick={() => openDeleteDestConfirm(dest)}
                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                data-testid={`delete-dest-${dest.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {destinations.length === 0 && (
        <div className="text-center py-12">
          <Globe className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No destinations available</p>
        </div>
      )}

      {/* Delete Destination Confirmation Dialog */}
      <Dialog open={showDeleteDestConfirm} onOpenChange={setShowDeleteDestConfirm}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Country
            </DialogTitle>
          </DialogHeader>
          {deletingDestination && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-300">
                Are you sure you want to delete <strong>{deletingDestination.country}</strong>?
              </p>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-700 dark:text-red-300">
                  <strong>Warning:</strong> This will also delete all universities and courses associated with this country. This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteDestConfirm(false)}
                  className="flex-1 border-gray-300 dark:border-slate-600 dark:text-gray-300"
                  disabled={isDeletingDest}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteDestination}
                  className="flex-1 bg-red-600 text-white hover:bg-red-700"
                  disabled={isDeletingDest}
                  data-testid="confirm-delete-dest"
                >
                  {isDeletingDest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete Country
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Add Destination Dialog */}
      <Dialog open={showAddDestination} onOpenChange={setShowAddDestination}>
        <DialogContent className="bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 dark:text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-teal-600" />
              Add New Country
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Country Name *</Label>
              <Input
                value={newDestination.country}
                onChange={(e) => setNewDestination({...newDestination, country: e.target.value})}
                placeholder="e.g., Australia"
                className="mt-1"
                data-testid="add-dest-country"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Flag Emoji</Label>
              <Input
                value={newDestination.flag_emoji}
                onChange={(e) => setNewDestination({...newDestination, flag_emoji: e.target.value})}
                placeholder="e.g., 🇦🇺"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Average Tuition Range</Label>
              <Input
                value={newDestination.avg_tuition_range}
                onChange={(e) => setNewDestination({...newDestination, avg_tuition_range: e.target.value})}
                placeholder="e.g., £10,000 - £30,000/year"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Currency</Label>
              <Input
                value={newDestination.currency}
                onChange={(e) => setNewDestination({...newDestination, currency: e.target.value})}
                placeholder="e.g., AUD"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Description</Label>
              <Textarea
                value={newDestination.description}
                onChange={(e) => setNewDestination({...newDestination, description: e.target.value})}
                placeholder="Brief description of studying in this country..."
                className="mt-1"
                rows={3}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowAddDestination(false)}
                className="flex-1"
                disabled={isAddingDest}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddDestination}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                disabled={isAddingDest || !newDestination.country}
                data-testid="confirm-add-dest"
              >
                {isAddingDest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Country
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DestinationsTab;
